import { Model } from 'infra/model'
import { computed, makeObservable, observable } from 'mobx'
import { DateZero, maybeParseDate } from 'utils/date-util'
import User from 'iam/models/User'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import Workspace from 'iam/models/Workspace'
import { PrimeUser } from 'iam/UserStore'
import {
  InventoryItemDto,
  InventoryItemPriceType,
  InventoryItemProVisibility,
  SupportingItemDto,
  SupportingItemSpecDto,
  SupportingItemSpecTypesDto,
} from '../dtos/inventory-item'
import { PrimeDocumentTypeByShortId } from '../../documents/DocumentTypeStore'
import DocumentType from '../../documents/DocumentType'
import { match } from 'utils/pattern-match-util'
import { ExtensionBillingInfo } from './components/InventoryItemForm/ManageExtensionBillingInfo/declarations'

/**
 * Indicates the manner in which the Inventory Item is priced.
 */
export enum InventoryItemPricing {
  /**
   * Indicates that the Inventory Item is charged as a fee, which is a flat
   * charge for the client.
   */
  Fee,

  /**
   * Indicates that the Inventory Item contains two independent flat charges
   * for both the client and pro.
   */
  Flat,

  /**
   * Indicates that the Inventory Item contains two independent rates that markup
   * the job's price and the pro payout amount.
   */
  Markup,

  /**
   * Indicates that the Inventory Item contains two independent rates that markdown
   * the job's price and the pro payout amount.
   */
  Markdown,
}

export default class InventoryItem extends Model<
  InventoryItemDto,
  InventoryItem
> {
  @observable id = ''
  @observable versionId = ''
  @observable shortId = ''
  @observable name = ''
  @observable description = ''
  @observable workspace?: Workspace = undefined
  @observable createdBy = User.none
  @observable version = 0
  @observable dateCreated = DateZero()
  @observable courtesyAmount = 0
  @observable price = 0
  @observable proPrice = 0
  @observable rootId = ''
  @observable archived = false
  @observable priceType = InventoryItemPricing.Flat
  @observable supportingItems: InventoryItemSupportingItem[] = []
  @observable proVisibility: InventoryItemProVisibility =
    InventoryItemProVisibility.VISIBLE

  @observable typeSpecification: InventoryItemTypeSpecification =
    new InventoryItemDefaultTypeSpecification()

  constructor(
    private primeWorkspace: PrimeWorkspace,
    private primeUser: PrimeUser,
    private primeDocumentTypeByShortId: PrimeDocumentTypeByShortId,
  ) {
    super()
    makeObservable(this)
  }

  @computed
  get hidden(): boolean {
    return this.proVisibility === InventoryItemProVisibility.HIDDEN
  }

  @computed
  get global(): boolean {
    return !this.workspace
  }

  @computed
  get override(): boolean {
    return !this.global && !!this.rootId
  }

  @computed
  get priceTypeToString(): string {
    return match(this.priceType, {
      [InventoryItemPricing.Fee]: () => 'Fee',
      [InventoryItemPricing.Flat]: () => 'Flat',
      [InventoryItemPricing.Markup]: () => 'Markup',
      [InventoryItemPricing.Markdown]: () => 'Markdown',
    })
  }

  @computed
  get serviceTypeToString(): string {
    return match(this.typeSpecification.type, {
      [InventoryItemType.Default]: () => 'Default',
      [InventoryItemType.Bookkeeping]: () => 'Bookkeeping',
      [InventoryItemType.Consultation]: () => 'Consultation',
      [InventoryItemType.Tax]: () => 'Tax',
      [InventoryItemType.Extension]: () => 'Extension',
    })
  }

  @computed
  get supportingItemsCount() {
    return this.supportingItems.length
  }

  parse(json: Partial<InventoryItemDto>): Partial<InventoryItem> {
    return {
      id: json.id,
      versionId: json.version_id,
      shortId: json.short_id,
      name: json.name,
      description: json.description,
      proVisibility: json.pro_visibility,
      archived: json.archived,
      workspace: json.workspace_id
        ? this.primeWorkspace({ id: json.workspace_id })
        : undefined,
      createdBy: this.primeUser({
        public_id: json.created_by,
      }),
      version: json.version,
      dateCreated: maybeParseDate(json.date_created),
      courtesyAmount: json.courtesy_amount,
      price: json.price
        ? this.mapPrice(json.price, json.price_type)
        : undefined,
      proPrice: json.pro_price
        ? this.mapPrice(json.pro_price, json.price_type)
        : undefined,
      rootId: json.root_id,
      priceType: json.price_type
        ? mapInventoryItemPriceType(json.price_type)
        : undefined,
      supportingItems: json.supporting_items
        ? json.supporting_items.map((item) => this.mapSupportingItem(item))
        : [],
      typeSpecification: json.type_specification,
    }
  }

  private mapSupportingItem(
    item: SupportingItemDto,
  ): InventoryItemSupportingItem {
    return {
      required: item.required,
      spec: this.mapSupportingItemSpec(item.spec),
    }
  }

  private mapSupportingItemSpec(
    spec: SupportingItemSpecDto,
  ): InventoryItemSupportingItemSpec {
    switch (spec.type) {
      case SupportingItemSpecTypesDto.DOCUMENT:
        return {
          type: InventoryItemSupportingItemSpecTypes.DOCUMENT,
          documentType:
            this.primeDocumentTypeByShortId(spec.document.type_short_id) ??
            DocumentType.none,
        }
      case SupportingItemSpecTypesDto.BUSINESS_INFO:
        return {
          type: InventoryItemSupportingItemSpecTypes.BUSINESS_INFO,
        }
      case SupportingItemSpecTypesDto.BUSINESS_REFUND_METHOD_INFO:
        return {
          type: InventoryItemSupportingItemSpecTypes.BUSINESS_REFUND_METHOD_INFO,
        }
      case SupportingItemSpecTypesDto.DEPENDENTS_INFO:
        return {
          type: InventoryItemSupportingItemSpecTypes.DEPENDENTS_INFO,
        }
      case SupportingItemSpecTypesDto.NOTES:
        return {
          type: InventoryItemSupportingItemSpecTypes.NOTES,
        }
      case SupportingItemSpecTypesDto.PERSONAL_INFO:
        return {
          type: InventoryItemSupportingItemSpecTypes.PERSONAL_INFO,
        }
      case SupportingItemSpecTypesDto.PERSONAL_REFUND_METHOD_INFO:
        return {
          type: InventoryItemSupportingItemSpecTypes.PERSONAL_REFUND_METHOD_INFO,
        }
      case SupportingItemSpecTypesDto.SPOUSAL_INFO:
        return {
          type: InventoryItemSupportingItemSpecTypes.SPOUSAL_INFO,
        }

      default:
        throw new Error('Unsupported supporting item spec type')
    }
  }

  /**
   * Maps the price taking into account that the API represents rate prices as decimals.
   */
  private mapPrice(price: number, type?: InventoryItemPriceType) {
    return type === InventoryItemPriceType.MARKUP ||
      type === InventoryItemPriceType.MARKDOWN
      ? price * 100
      : price
  }
}

export enum InventoryItemExtensionType {
  Personal,
  Business,
  Outsourcing,
}

export enum InventoryItemExtensionBillingType {
  Percentage,
  FlatFee,
}

export enum ExtensionBillingInfoType {
  Regular,
  Progression,
}

export enum InventoryItemType {
  Default,
  Bookkeeping,
  Consultation,
  Tax,
  Extension,
}

export interface InventoryItemTypeSpecification {
  type: InventoryItemType
}

export class InventoryItemDefaultTypeSpecification
  implements InventoryItemTypeSpecification
{
  type: InventoryItemType = InventoryItemType.Default
}

export class InventoryItemBookkeepingTypeSpecification
  implements InventoryItemTypeSpecification
{
  type: InventoryItemType = InventoryItemType.Bookkeeping

  @observable recurring: boolean = false

  constructor(recurring: boolean) {
    makeObservable(this)
    this.recurring = recurring
  }
}

export class InventoryItemConsultationTypeSpecification
  implements InventoryItemTypeSpecification
{
  type: InventoryItemType = InventoryItemType.Consultation

  @observable lengthInMinutes: number = 0

  constructor(lengthInMinutes: number) {
    makeObservable(this)
    this.lengthInMinutes = lengthInMinutes
  }
}

export class InventoryItemTaxTypeSpecification
  implements InventoryItemTypeSpecification
{
  type: InventoryItemType = InventoryItemType.Tax

  @observable reviewPrice: number = 0
  @observable reviewPayout: number = 0

  constructor(reviewPrice: number, reviewPayout: number) {
    makeObservable(this)
    this.reviewPrice = reviewPrice
    this.reviewPayout = reviewPayout
  }
}

export class InventoryItemExtensionTypeSpecification
  implements InventoryItemTypeSpecification
{
  type: InventoryItemType = InventoryItemType.Extension

  extensionType: InventoryItemExtensionType =
    InventoryItemExtensionType.Personal

  billingType: InventoryItemExtensionBillingType =
    InventoryItemExtensionBillingType.Percentage

  billingInfo: ExtensionBillingInfo

  constructor(
    extensionType: InventoryItemExtensionType,
    billingType: InventoryItemExtensionBillingType,
    billingInfo: ExtensionBillingInfo,
  ) {
    this.extensionType = extensionType
    this.billingType = billingType
    this.billingInfo = billingInfo
  }

  get billingTypeToString() {
    return match(this.billingType, {
      [InventoryItemExtensionBillingType.FlatFee]: () => 'Flat Fee',
      [InventoryItemExtensionBillingType.Percentage]: () => 'Percentage',
    })
  }
}

export interface InventoryItemSupportingItem {
  required: boolean
  spec: InventoryItemSupportingItemSpec
}

export type InventoryItemSupportingItemSpec =
  | InventoryItemSupportingItemDocumentSpec
  | InventoryItemSupportingItemPersonalInfoSpec
  | InventoryItemSupportingItemSpousalInfoSpec
  | InventoryItemSupportingItemDependentsInfoSpec
  | InventoryItemSupportingItemPersonalRefundMethodInfoSpec
  | InventoryItemSupportingItemBusinessInfoSpec
  | InventoryItemSupportingItemBusinessRefundMethodInfoSpec
  | InventoryItemSupportingItemNotesSpec

export interface InventoryItemSupportingItemDocumentSpec {
  type: InventoryItemSupportingItemSpecTypes.DOCUMENT
  documentType: DocumentType
}

export interface InventoryItemSupportingItemPersonalInfoSpec {
  type: InventoryItemSupportingItemSpecTypes.PERSONAL_INFO
}

export interface InventoryItemSupportingItemSpousalInfoSpec {
  type: InventoryItemSupportingItemSpecTypes.SPOUSAL_INFO
}

export interface InventoryItemSupportingItemDependentsInfoSpec {
  type: InventoryItemSupportingItemSpecTypes.DEPENDENTS_INFO
}

export interface InventoryItemSupportingItemPersonalRefundMethodInfoSpec {
  type: InventoryItemSupportingItemSpecTypes.PERSONAL_REFUND_METHOD_INFO
}

export interface InventoryItemSupportingItemBusinessInfoSpec {
  type: InventoryItemSupportingItemSpecTypes.BUSINESS_INFO
}

export interface InventoryItemSupportingItemBusinessRefundMethodInfoSpec {
  type: InventoryItemSupportingItemSpecTypes.BUSINESS_REFUND_METHOD_INFO
}

export interface InventoryItemSupportingItemNotesSpec {
  type: InventoryItemSupportingItemSpecTypes.NOTES
}

export enum InventoryItemSupportingItemSpecTypes {
  DOCUMENT,
  PERSONAL_INFO,
  SPOUSAL_INFO,
  DEPENDENTS_INFO,
  PERSONAL_REFUND_METHOD_INFO,
  BUSINESS_INFO,
  BUSINESS_REFUND_METHOD_INFO,
  NOTES,
}

export function formatSupportingItemSpec(
  spec: InventoryItemSupportingItemSpec,
): string {
  switch (spec.type) {
    case InventoryItemSupportingItemSpecTypes.DOCUMENT:
      return spec.documentType.name
    case InventoryItemSupportingItemSpecTypes.PERSONAL_INFO:
      return 'Personal Info'
    case InventoryItemSupportingItemSpecTypes.SPOUSAL_INFO:
      return 'Spousal Info'
    case InventoryItemSupportingItemSpecTypes.DEPENDENTS_INFO:
      return 'Dependents Info'
    case InventoryItemSupportingItemSpecTypes.PERSONAL_REFUND_METHOD_INFO:
      return 'Personal Refund Method Info'
    case InventoryItemSupportingItemSpecTypes.BUSINESS_INFO:
      return 'Business Info'
    case InventoryItemSupportingItemSpecTypes.BUSINESS_REFUND_METHOD_INFO:
      return 'Business Refund Method Info'
    case InventoryItemSupportingItemSpecTypes.NOTES:
      return 'Notes'
    default:
      return '(Unknown item)'
  }
}

/**
 * Maps from the DTO Price Type to a domain-representation.
 */
function mapInventoryItemPriceType(priceType: InventoryItemPriceType) {
  return match(priceType, {
    [InventoryItemPriceType.FEE]: () => InventoryItemPricing.Fee,
    [InventoryItemPriceType.FLAT]: () => InventoryItemPricing.Flat,
    [InventoryItemPriceType.MARKUP]: () => InventoryItemPricing.Markup,
    [InventoryItemPriceType.MARKDOWN]: () => InventoryItemPricing.Markdown,
  })
}
